import SiteLayout from "@/components/feature/SiteLayout";
import PageMeta from "@/components/feature/PageMeta";
import PageHero from "@/components/feature/PageHero";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import { principles, siteInfo } from "@/mocks/site";
import TipForm from "@/pages/about/components/TipForm";

const positioning = [
  {
    icon: "ri-focus-3-line",
    title: "我们做什么",
    desc: "把关于 511 爆料 的公开信息按时间线重新排列，标注来源等级，做证据对照与细节补充。",
  },
  {
    icon: "ri-forbid-2-line",
    title: "我们不做什么",
    desc: "不替读者下道德判断，不推测没有出处的细节，不把存疑内容包装成已确认的事实。",
  },
  {
    icon: "ri-open-source-line",
    title: "为什么公开方法",
    desc: "方法和材料一起公开，读者才能自己复核。可信不是靠声明，而是靠可以被检查。",
  },
];

const testimonials = [
  {
    quote:
      "之前在各种群里看了一堆互相打架的说法，到这里才看清哪些有出处、哪些是转发时加的。对照表省了我好多时间。",
    author: "读者 · 阿舟",
    role: "持续关注事件进展",
  },
  {
    quote:
      "最喜欢的是它把「存疑」写得很清楚，而不是硬给一个结论。材料不足就说不足，这点很难得。",
    author: "读者 · 林女士",
    role: "求证笔记读者",
  },
  {
    quote:
      "我提交过一份更早的原始文件，三天后就在时间线里看到了更新，还标注了修改原因。这种可追溯的感觉很好。",
    author: "读者 · 陈同学",
    role: "线索提供者",
  },
];

export default function AboutPage() {
  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: "关于 511 求证档案",
      description:
        "511 求证档案的定位、发展理念、内容原则与联系方式，一个只做来源标注与证据对照的公开信息整理站点。",
      inLanguage: "zh-CN",
    },
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: siteInfo.name,
      description:
        "围绕 511 爆料 做细节补充、时间线整理、来源求实与证据对照的公开信息整理站点。",
      email: siteInfo.email,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://balha.cn/" },
        {
          "@type": "ListItem",
          position: 2,
          name: "关于我们",
          item: "https://balha.cn/about",
        },
      ],
    },
  ];

  return (
    <SiteLayout>
      <PageMeta
        title="关于511求证档案｜定位、内容原则与联系方式"
        description="了解511求证档案的品牌定位、发展理念与内容原则。我们只做511爆料细节补充、时间线整理、来源求实与证据对照，并公开联系方式接收线索。"
        keywords="511求证档案,511爆料来源求实,关于我们,内容原则,联系方式"
        path="/about"
        schema={schema}
      />

      <PageHero
        eyebrow="ABOUT"
        title="关于 511 求证档案"
        description="我们不是一家媒体，也不是一个匿名爆料号。我们只做一件事：把公开信息整理成可以复核的档案。"
        crumbs={
          <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
        }
      />

      <section className="w-full px-4 py-12 md:px-6 md:py-16 lg:px-10">
        <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-2 lg:gap-12">
          <div className="w-full">
            <p className="text-xs font-medium tracking-[0.25em] text-primary-700">
              BRAND STORY
            </p>
            <h2 className="mt-3 font-heading text-xl font-bold leading-snug text-foreground-950 md:text-3xl">
              从「转发到哪一步变了样」开始
            </h2>
            <div className="mt-4 flex flex-col gap-3 text-sm leading-loose text-foreground-600 md:text-base">
              <p>
                511 求证档案 最初只是一个私人整理的表格。整理者发现，同一张截图在不同群里被转发几轮之后，
                文字说明会多出原文没有的细节，而这些细节又会被下一轮引用，慢慢变成「大家都这么说」。
              </p>
              <p>
                于是表格变成了档案。我们开始为每条说法追来源、定等级、做对照，并把过程公开出来。
                目的不是给出一个权威结论，而是让每个愿意认真看的人，都能顺着材料自己走一遍。
              </p>
              <p>
                现在档案由一支小规模志愿编辑组维护，统一使用北京时间，内容以公开材料为基础，
                所有条目都保留修订记录。
              </p>
            </div>
          </div>

          <div className="h-[260px] w-full overflow-hidden rounded-lg border border-background-300 bg-background-200 md:h-[380px]">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20minimal%20editorial%20desk%20scene%20with%20open%20notebook%20of%20handwritten%20timeline%20notes%2C%20scattered%20paper%20clippings%20and%20a%20pen%2C%20rust%20and%20olive%20accents%2C%20cream%20paper%20background%2C%20soft%20natural%20light&width=1000&height=800&seq=qz-about-main&orientation=landscape"
              alt="511求证档案编辑部：手写时间线笔记与材料整理"
              title="关于511求证档案｜材料整理与来源标注"
              className="h-full w-full object-cover object-top"
            />
          </div>
        </div>
      </section>

      <section className="w-full border-y border-background-200 bg-background-100 px-4 py-12 md:px-6 md:py-16 lg:px-10">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {positioning.map((item, index) => (
            <div
              key={item.title}
              className={`rounded-lg border p-5 ${
                index === 1
                  ? "border-primary-200 bg-primary-100"
                  : "border-background-200 bg-background-50"
              }`}
            >
              <span
                className={`flex h-10 w-10 items-center justify-center rounded-md ${
                  index === 1
                    ? "bg-primary-500 text-background-50"
                    : "bg-accent-100 text-accent-800"
                }`}
              >
                <i className={`${item.icon} text-xl`}></i>
              </span>
              <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">
                {item.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                {item.desc}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="w-full px-4 py-12 md:px-6 md:py-16 lg:px-10">
        <div className="mb-8">
          <p className="text-xs font-medium tracking-[0.25em] text-primary-700">
            CONTENT PRINCIPLES
          </p>
          <h2 className="mt-2 font-heading text-xl font-bold tracking-tight text-foreground-950 md:text-2xl">
            我们的内容原则
          </h2>
        </div>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          {principles.map((item, index) => (
            <div
              key={item.title}
              className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-5"
            >
              <span className="font-heading text-2xl font-black text-primary-300">
                {String(index + 1).padStart(2, "0")}
              </span>
              <div>
                <h3 className="font-heading text-base font-semibold text-foreground-950">
                  {item.title}
                </h3>
                <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">
                  {item.desc}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="w-full border-y border-background-200 bg-background-100 px-4 py-12 md:px-6 md:py-16 lg:px-10">
        <div className="mb-8">
          <p className="text-xs font-medium tracking-[0.25em] text-primary-700">
            READER FEEDBACK
          </p>
          <h2 className="mt-2 font-heading text-xl font-bold tracking-tight text-foreground-950 md:text-2xl">
            读者怎么说
          </h2>
        </div>
        <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
          {testimonials.map((item) => (
            <figure
              key={item.author}
              className="flex flex-col rounded-lg border border-background-200 bg-background-50 p-5"
            >
              <span className="flex h-6 w-6 items-center justify-center text-primary-300">
                <i className="ri-double-quotes-l text-2xl"></i>
              </span>
              <blockquote className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700">
                {item.quote}
              </blockquote>
              <figcaption className="mt-4 border-t border-background-200 pt-3">
                <p className="font-heading text-sm font-semibold text-foreground-950">
                  {item.author}
                </p>
                <p className="text-xs text-foreground-500">{item.role}</p>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      <section id="tips" className="w-full scroll-mt-28 px-4 py-12 md:px-6 md:py-16 lg:px-10">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3 lg:gap-10">
          <div className="lg:col-span-1">
            <p className="text-xs font-medium tracking-[0.25em] text-primary-700">
              CONTACT & TIPS
            </p>
            <h2 className="mt-2 font-heading text-xl font-bold tracking-tight text-foreground-950 md:text-2xl">
              联系我们 / 提交线索
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              如果你掌握与 511 爆料 有关的补充材料、更早来源，或发现我们的标注存在问题，欢迎通过表单提交。
              我们会把它加入对照表并重新评估相关条目。
            </p>
            <ul className="mt-6 flex flex-col gap-3 text-sm text-foreground-600">
              <li className="flex items-center gap-2">
                <span className="flex h-5 w-5 items-center justify-center text-accent-600">
                  <i className="ri-mail-line text-base"></i>
                </span>
                <a
                  href={`mailto:${siteInfo.email}`}
                  className="break-all transition-colors hover:text-primary-700"
                >
                  {siteInfo.email}
                </a>
              </li>
              <li className="flex items-center gap-2">
                <span className="flex h-5 w-5 items-center justify-center text-accent-600">
                  <i className="ri-time-line text-base"></i>
                </span>
                一般会在 3 个工作日内回复核对
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-0.5 flex h-5 w-5 items-center justify-center text-accent-600">
                  <i className="ri-shield-check-line text-base"></i>
                </span>
                联系方式仅用于核对，不会公开或用于其他用途
              </li>
            </ul>

            <div className="mt-6 rounded-lg border border-accent-300 bg-accent-100 p-4">
              <p className="text-xs leading-relaxed text-accent-900">
                <span className="font-medium">提交前提示：</span>
                请尽量附上可回查的原始出处。只提供转述、没有出处的信息，我们只能登记为线索，
                无法提升可信度等级。
              </p>
            </div>
          </div>

          <div className="lg:col-span-2">
            <TipForm />
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}